/**
 * Logger utility that only logs in development mode
 * Logs are disabled in production regardless of the URL
 * Errors are automatically sent to Sentry for monitoring
 */

import * as Sentry from '@sentry/react'

const isDevelopment = import.meta.env.DEV // Vite's built-in env variable

class Logger {
  private enabled: boolean

  constructor() {
    // Only enable logs if we're in development mode
    // This is determined by Vite's build mode, not by the URL
    this.enabled = isDevelopment
  }

  log(...args: unknown[]) {
    if (this.enabled) {
      console.log(...args)
    }
  }

  error(...args: unknown[]) {
    if (this.enabled) {
      console.error(...args)
    }
    
    // Only send errors to Sentry in production
    const isProduction = import.meta.env.VITE_MODE === 'prod' && !import.meta.env.DEV
    if (isProduction) {
      const error = args[0]
      if (error instanceof Error) {
        Sentry.captureException(error, {
          extra: {
            additionalArgs: args.slice(1),
          },
        })
      } else if (typeof error === 'string') {
        Sentry.captureMessage(error, 'error')
      }
    }
  }

  warn(...args: unknown[]) {
    if (this.enabled) {
      console.warn(...args)
    }
    
    // Only add breadcrumbs in production
    const isProduction = import.meta.env.VITE_MODE === 'prod' && !import.meta.env.DEV
    if (isProduction) {
      Sentry.addBreadcrumb({
        message: args.map(arg => String(arg)).join(' '),
        level: 'warning',
        category: 'logger',
      })
    }
  }

  info(...args: unknown[]) {
    if (this.enabled) {
      console.info(...args)
    }
    
    // Only add breadcrumbs in production
    const isProduction = import.meta.env.VITE_MODE === 'prod' && !import.meta.env.DEV
    if (isProduction) {
      Sentry.addBreadcrumb({
        message: args.map(arg => String(arg)).join(' '),
        level: 'info',
        category: 'logger',
      })
    }
  }

  debug(...args: unknown[]) {
    if (this.enabled) {
      console.debug(...args)
    }
  }

  group(...args: unknown[]) {
    if (this.enabled) {
      console.group(...args)
    }
  }

  groupEnd() {
    if (this.enabled) {
      console.groupEnd()
    }
  }

  table(...args: unknown[]) {
    if (this.enabled) {
      console.table(...args)
    }
  }
}

export const logger = new Logger()